import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Avatar,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  CalendarMonth,
  PendingActions,
  CloudUpload,
  Grading,
} from "@mui/icons-material";
import { useAssignments, useSubmitAssignment } from "../../hooks/queries/useAssignments";
import DataTable from "../../components/data-display/DataTable";
import StatsCard from "../../components/data-display/StatsCard";
import StatusChip from "../../components/data-display/StatusChip";
import FilterTabs from "../../components/navigation/FilterTabs";
import FormDialog from "../../components/forms/FormDialog";
import ViewDialog from "../../components/forms/ViewDialog";
import FormFileUpload from "../../components/forms/FormFileUpload";
import EmptyState from "../../components/data-display/EmptyState";
import { TextField } from "@mui/material";
import { downloadBlob } from "../../utils/download";

const Assignments = () => {
  const { data: assignmentsData, isLoading: loading } = useAssignments({});
  const submitMutation = useSubmitAssignment();
  const assignments = assignmentsData?.assignments || [];
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [submissionTitle, setSubmissionTitle] = useState("");
  const [comments, setComments] = useState("");

  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleTitleChange = (e) => {
    const value = e.target.value.replace(/\d/g, "").slice(0, 50);
    setSubmissionTitle(value);
  };
  const titleError = submissionTitle.length > 0 && submissionTitle.length < 3;
  const titleHelper = submissionTitle.length < 3
    ? submissionTitle.length > 0
      ? "Min 3 characters"
      : "Min 3, max 50 characters"
    : `${submissionTitle.length}/50`;
  const normalizedAssignments = assignments.map((a) => ({
    ...a,
    title: a.title || a.name || "-",
    teacher: a.teacher || a.teacherName || "-",
    classDisplay: a.classDisplayName || (a.class && a.classSection ? `${a.class} - ${a.classSection}` : a.class) || "-",
    totalMarks: a.totalMarks ?? a.maxMarks ?? 0,
    status: a.status || "pending",
    description: a.description || a.details || "-",
  }));

  const filteredAssignments = normalizedAssignments.filter((assignment) => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return assignment.status === "pending";
    if (activeTab === 2) return assignment.status === "submitted";
    if (activeTab === 3) return assignment.status === "graded";
    return true;
  });

  const handleSubmit = () => {
    if (submissionTitle.trim().length < 3) return;
    if (!selectedAssignment) return;
    submitMutation.mutate(
      {
        assignmentId: selectedAssignment.id,
        submissionData: { files, comments, title: submissionTitle.trim() },
      },
      {
        onSuccess: () => {
          setSubmitDialogOpen(false);
          setFiles([]);
          setSubmissionTitle("");
          setComments("");
          setSelectedAssignment(null);
        },
      }
    );
  };

  const columns = [
    {
      field: "title",
      headerName: "Assignment",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: "primary.light", width: 32, height: 32 }}>
            <AssignmentIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.subject}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "teacher", headerName: "Teacher", width: 150 },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarMonth sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    { field: "totalMarks", headerName: "Marks", width: 80 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 100,
      renderCell: (params) =>
        params.row.status === "graded" ? (
          <Typography variant="body2" fontWeight={600}>
            {params.value}/{params.row.totalMarks}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedAssignment(params.row);
              setViewDialogOpen(true);
            }}
          >
            View
          </Button>
          {params.row.status === "pending" && (
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAssignment(params.row);
                setSubmitDialogOpen(true);
              }}
            >
              Submit
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const stats = [
    {
      label: "Total",
      value: normalizedAssignments.length,
      color: "primary",
      icon: <AssignmentIcon />,
    },
    {
      label: "Pending",
      value: normalizedAssignments.filter((a) => a.status === "pending").length,
      color: "warning",
      icon: <PendingActions />,
    },
    {
      label: "Submitted",
      value: normalizedAssignments.filter((a) => a.status === "submitted").length,
      color: "info",
      icon: <CloudUpload />,
    },
    {
      label: "Graded",
      value: normalizedAssignments.filter((a) => a.status === "graded").length,
      color: "success",
      icon: <Grading />,
    },
  ];

  const tabs = [
    { label: "All" },
    { label: "Pending", count: stats[1].value },
    { label: "Submitted", count: stats[2].value },
    { label: "Graded", count: stats[3].value },
  ];

  const resolveAttachmentUrl = (entry) => {
    if (!entry) return "";
    if (typeof entry === "string") {
      const s = entry.trim();
      return s.startsWith("http://") || s.startsWith("https://") ? s : "";
    }
    const candidates = [entry.url, entry.signedUrl, entry.fileUrl, entry.downloadUrl].filter(Boolean);
    for (const c of candidates) {
      const s = String(c).trim();
      if (s.startsWith("http://") || s.startsWith("https://")) return s;
    }
    return "";
  };

  const handleDownloadAssignmentAttachments = async () => {
    const raw = selectedAssignment?.originalData || selectedAssignment || {};
    const list = Array.isArray(raw.attachments) ? raw.attachments : [];
    const urls = list.map((a) => resolveAttachmentUrl(a)).filter(Boolean);
    const legacy = resolveAttachmentUrl(raw.attachment);
    if (legacy) urls.unshift(legacy);
    if (!urls.length) return;
    for (let i = 0; i < urls.length; i += 1) {
      try {
        const response = await fetch(urls[i], { mode: "cors" });
        if (!response.ok) throw new Error("Download failed");
        const blob = await response.blob();
        const name =
          list[i]?.name ||
          list[i]?.originalName ||
          raw.name ||
          `assignment_attachment_${i + 1}`;
        downloadBlob(blob, String(name).replace(/[/\\?%*:|"<>]/g, "_"), blob.type || "application/octet-stream");
      } catch {
        window.open(urls[i], "_blank", "noopener,noreferrer");
      }
    }
  };

  const assignmentAttachmentUrls = (() => {
    const raw = selectedAssignment?.originalData || selectedAssignment || {};
    const list = Array.isArray(raw.attachments) ? raw.attachments : [];
    const urls = list.map((a) => resolveAttachmentUrl(a)).filter(Boolean);
    const legacy = resolveAttachmentUrl(raw.attachment);
    if (legacy) urls.unshift(legacy);
    return urls;
  })();

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <StatsCard
              value={stat.value}
              label={stat.label}
              icon={stat.icon}
              color={stat.color}
            />
          </Grid>
        ))}
      </Grid>

      {/* Main Card */}
      <Card>
        <CardContent>
          <FilterTabs
            value={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
              Loading...
            </Typography>
          ) : filteredAssignments.length === 0 ? (
            <EmptyState icon={<></>} title="No assignments found" message="There are no assignments to display right now." sx={{ py: 5 }} />
          ) : (
            <DataTable rows={filteredAssignments} columns={columns} loading={false} />
          )}
        </CardContent>
      </Card>

      {/* Submit Dialog */}
      <FormDialog
        open={submitDialogOpen}
        title="Submit Assignment"
        onClose={() => {
          setSubmitDialogOpen(false);
          setFiles([]);
          setSubmissionTitle("");
          setComments("");
        }}
        onSubmit={handleSubmit}
        loading={submitMutation.isPending}
        submitLabel="Submit"
        disableSubmit={submissionTitle.trim().length < 3}
      >
        {selectedAssignment && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {selectedAssignment.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {selectedAssignment.description}
            </Typography>

            <TextField
              fullWidth
              label="Title"
              value={submissionTitle}
              onChange={handleTitleChange}
              onKeyDown={blockNumberKeyDown}
              error={titleError}
              helperText={titleHelper}
              inputProps={{ maxLength: 50 }}
              placeholder="e.g. My Essay Submission"
              sx={{ mb: 2 }}
            />

            <FormFileUpload
              name="files"
              value={files}
              onChange={setFiles}
              multiple
              label="Upload Files *"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4"
              maxSize={100}
            />

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Comments (Optional)"
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </Box>
          </Box>
        )}
      </FormDialog>

      {/* View Dialog - only mount when open; close is deferred so click doesn't reopen anything */}
      {viewDialogOpen && selectedAssignment && (
        <ViewDialog
          open={true}
          title="Assignment Details"
          onClose={() => {
            requestAnimationFrame(() => setViewDialogOpen(false));
          }}
          fields={[
            { label: "Subject", value: selectedAssignment.subject },
            { label: "Teacher", value: selectedAssignment.teacher },
            { label: "Class", value: selectedAssignment.classDisplay || selectedAssignment.class || "-" },
            { label: "Due Date", value: selectedAssignment.dueDate },
            {
              label: "Total Marks",
              value: selectedAssignment.totalMarks,
            },
            {
              label: "Status",
              value: <StatusChip status={selectedAssignment.status} />,
            },
            {
              label: "Description",
              value: selectedAssignment.description,
              span: 12,
            },
            ...(selectedAssignment.status === "graded"
              ? [
                  {
                    label: "Grade",
                    value: `${selectedAssignment.grade}/${selectedAssignment.totalMarks}`,
                  },
                  {
                    label: "Feedback",
                    value: selectedAssignment.feedback,
                    span: 12,
                  },
                ]
              : []),
          ]}
          downloadLabel="Download Attachments"
          onDownload={assignmentAttachmentUrls.length > 0 ? handleDownloadAssignmentAttachments : undefined}
        />
      )}
    </Box>
  );
};

export default Assignments;
